/**
 * Recovers from stale dynamic-import / lazy-chunk failures that happen when
 * the served module graph changes while a tab is still open (common during
 * Vite HMR and immediately after a deploy).
 *
 * Strategy: detect the error → unregister SW caches → reload once.
 * A sessionStorage guard prevents reload loops if the chunk is genuinely broken.
 */

const RETRY_KEY = "__chunk_reload_attempt__";

const CHUNK_ERROR_PATTERNS = [
  "Loading chunk",
  "Failed to fetch dynamically imported module",
  "Importing a module script failed",
  "error loading dynamically imported module",
  "ChunkLoadError",
];

export function isChunkLoadError(input: unknown): boolean {
  if (!input) return false;
  const msg =
    typeof input === "string"
      ? input
      : (input as any)?.message || (input as any)?.reason?.message || String(input);
  if (!msg) return false;
  return CHUNK_ERROR_PATTERNS.some((p) => msg.includes(p));
}

export function recoverFromChunkError(reason: string = "chunk-error"): void {
  try {
    const already = sessionStorage.getItem(RETRY_KEY);
    if (already) {
      // Already tried once this session — let the error surface so a real bug
      // doesn't get hidden behind an infinite reload.
      console.error(`[chunkErrorHandler] Skipping retry (${reason}); already attempted.`);
      return;
    }
    sessionStorage.setItem(RETRY_KEY, String(Date.now()));
  } catch {
    /* sessionStorage may be unavailable (private mode) — proceed anyway */
  }

  console.warn(`[chunkErrorHandler] Stale chunk detected (${reason}); reloading once…`);

  // Best-effort: drop any service worker that may be serving stale assets
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker
      .getRegistrations()
      .then((regs) => Promise.all(regs.map((r) => r.unregister())))
      .catch(() => undefined)
      .finally(() => window.location.reload());
    return;
  }

  window.location.reload();
}

/**
 * Clears the one-shot guard once the page has successfully loaded after a
 * recovery, so the next genuine stale-chunk event can also auto-recover.
 */
function clearRetryGuardOnSuccess(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("load", () => {
    // Wait a tick to make sure lazy chunks resolved
    setTimeout(() => {
      try {
        sessionStorage.removeItem(RETRY_KEY);
      } catch {
        /* ignore */
      }
    }, 2000);
  });
}

export function setupChunkErrorHandler(): void {
  if (typeof window === "undefined") return;

  // Vite's official event for failed module preloads
  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault?.();
    recoverFromChunkError("vite:preloadError");
  });

  // Generic uncaught script errors
  window.addEventListener("error", (event) => {
    if (isChunkLoadError(event.message) || isChunkLoadError(event.error)) {
      recoverFromChunkError("window.error");
    }
  });

  // Lazy()/dynamic import() rejections bubble up here
  window.addEventListener("unhandledrejection", (event) => {
    if (isChunkLoadError(event.reason)) {
      event.preventDefault?.();
      recoverFromChunkError("unhandledrejection");
    }
  });

  clearRetryGuardOnSuccess();
}
